/**
 * Optionaler Akte-Wizard-Schritt „Element-Raum“.
 * Klasse ELEMENT_AKTE_ROOM_OPTIONAL: kein POST beim reinen Akte-CREATE.
 * Erfolg nur mit ehrlicher room_id und derselben akte_id (Stub zählt nicht).
 * Nachjoinen bleibt der bestehende Raum-anlegen-Weg im Kanzlei-Chat.
 */

import { seatHasInvitableElementAccount } from './elementSeatInvite.ts';
import { isHonestMatrixRoomId, isStubMatrixRoomId } from './portalMatrixOrigins.ts';

export const AKTE_WIZARD_STEPS = [
  { step: 1 as const, label: 'Basis' },
  { step: 2 as const, label: 'Checkliste' },
  { step: 3 as const, label: 'Dokument' },
  { step: 4 as const, label: 'Personen' },
  { step: 5 as const, label: 'Element' },
];

export const AKTE_ELEMENT_ROOM_ENDPOINT = '/api/arkorbit/element-rooms';

export type AkteWizardStep = (typeof AKTE_WIZARD_STEPS)[number]['step'];

export type AkteWizardRoomSeat = {
  id: string;
  element_seat?: boolean;
  /** Leer oder Stub: nicht in seat_ids. Der Hook würde sonst 400. */
  matrix_user_id?: string;
};

export type AkteElementRoomBody = {
  name: string;
  seat_ids: string[];
  akte_id: string;
};

export type AkteElementRoomPlan =
  | { post: false }
  | { post: true; ok: false; message: string }
  | { post: true; ok: true; body: AkteElementRoomBody };

/** Sichtbarer Vorschlag aus dem Aktennamen, vor dem Senden änderbar. */
export function suggestAkteElementRoomName(akteTitle: string): string {
  const title = String(akteTitle || '')
    .replace(/[\r\n]+/g, ' ')
    .trim();
  if (!title) return '';
  return title.length > 120 ? title.slice(0, 120).trim() : title;
}

function elementSeatIds(seats: AkteWizardRoomSeat[]): string[] {
  const out: string[] = [];
  const seen: Record<string, true> = {};
  for (let i = 0; i < seats.length; i++) {
    const seat = seats[i];
    if (!seatHasInvitableElementAccount(seat)) continue;
    const id = String(seat.id || '').trim();
    if (!id || seen[id] || id.charAt(0) === '@') continue;
    seen[id] = true;
    out.push(id);
    if (out.length > 50) break;
  }
  return out;
}

/**
 * Ohne den Schritt: kein Body und kein POST.
 * Mit Schritt: nur angehakte Sitze mit Element-Konto. Der Ersteller bleibt draußen — der Hook nimmt auth.id.
 * Sitze ohne matrix_user_id (office@) gehen nicht in seat_ids.
 * Nachladen in einen bestehenden Raum bleibt die Spalte im Kanzlei-Chat.
 */
export function planAkteElementRoom(input: {
  includeRoom: boolean;
  name: string;
  akteId: string;
  seats: AkteWizardRoomSeat[];
  /** customers.id des angemeldeten Sitzes. Nicht in seat_ids. */
  excludeSeatId?: string;
}): AkteElementRoomPlan {
  if (!input.includeRoom) return { post: false };
  const name = String(input.name || '').trim();
  if (!name || name.length > 120 || /[\r\n]/.test(name)) {
    return { post: true, ok: false, message: 'Raumname fehlt oder ist ungültig.' };
  }
  const akteId = String(input.akteId || '').trim();
  if (!akteId || akteId.charAt(0) === '@' || akteId.length > 64 || /\s/.test(akteId)) {
    return { post: true, ok: false, message: 'Akte ist noch nicht angelegt.' };
  }
  const exclude = String(input.excludeSeatId || '').trim();
  const seat_ids = elementSeatIds(input.seats || []).filter((id) => id !== exclude);
  return { post: true, ok: true, body: { name, seat_ids, akte_id: akteId } };
}

/** Stub, fehlende oder fremde akte_id ist kein Erfolg. */
export function judgeAkteElementRoomResponse(
  data: Record<string, unknown> | null | undefined,
  requestedAkteId: string,
): { ok: true; roomId: string } | { ok: false; message: string } {
  const rec = data && typeof data === 'object' ? data : {};
  const roomId = typeof rec.room_id === 'string' ? rec.room_id.trim() : '';
  const akteId = typeof rec.akte_id === 'string' ? rec.akte_id.trim() : '';
  const wanted = String(requestedAkteId || '').trim();
  if (
    rec.ok !== true ||
    !wanted ||
    akteId !== wanted ||
    !isHonestMatrixRoomId(roomId) ||
    isStubMatrixRoomId(roomId) ||
    !Array.isArray(rec.invited_seat_ids)
  ) {
    const err = typeof rec.error === 'string' ? rec.error.trim() : '';
    const message = typeof rec.message === 'string' ? rec.message.trim() : '';
    return { ok: false, message: err || message || 'Raum nicht angelegt.' };
  }
  return { ok: true, roomId };
}
